// EP-161 §2.3 (D7), S6 (T161-W02a): the gate buttons of ที่จอดรถวันนี้, in
// the operator mobile app only. "เช็กเอาต์" on a parked car charges it and
// shows the QR; "รับเงินสด" on a car that owes (awaiting payment, or an
// overstay the kiosk cannot charge) takes the cash with a photo of it. On a
// desktop browser the same rows read "ใช้แอป FlowRent บนมือถือ" instead: the
// server cannot tell the app from a browser, so this rule is the UI's.
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { VerifyPaymentModal } from '@/components/payments/VerificationActionModals'
import { useCheckOutParkingSession, useInvalidateParkingSessions } from '@/hooks/useParkingSessions'
import { isNativePlatform } from '@/lib/platform'
import { useNotificationStore } from '@/stores'
import type { ParkingSession } from '@/types/parking'
import { CashSettleModal } from './CashSettleModal'
import { GateCheckOutSheet } from './GateCheckOutSheet'

// Check-out refusals with their own copy; anything else reads as failed.
const CHECK_OUT_ERRORS = ['already_exited', 'no_fee_configured'] as const

export type GateStep =
  | { kind: 'sheet'; session: ParkingSession }
  | { kind: 'cash'; session: ParkingSession }
  | { kind: 'verify'; session: ParkingSession }

export interface GateActionsProps {
  session: ParkingSession
  /** Owner, admin and staff; the route keeps the accountant out. */
  canAct: boolean
  /**
   * Opens the next step on the page (GateFlow). The steps live on the page,
   * not in the row, because a check-out moves the row out of a "parked" tab.
   */
  onStep: (step: GateStep) => void
}

function errorCode(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: { code?: string } } } }).response?.data?.error?.code
}

/** The buttons in a row's gateActions slot. */
export function GateActions({ session, canAct, onStep }: GateActionsProps) {
  const { t } = useTranslation('parking')
  const addToast = useNotificationStore((s) => s.addToast)
  const checkOut = useCheckOutParkingSession()

  const canCheckOut = session.state === 'parked'
  const canTakeCash = session.state === 'awaiting_payment' || session.state === 'overstay'
  if (!canAct || (!canCheckOut && !canTakeCash)) return null

  if (!isNativePlatform()) {
    return (
      <span className="text-xs text-base-content/60 self-center" data-testid="gate-use-mobile-app">
        {t('sessions.gate.use_mobile_app')}
      </span>
    )
  }

  // mutateAsync, not mutate with callbacks: the row may unmount once the
  // list refetches, and the next step must still open.
  const startCheckOut = async () => {
    try {
      const row = await checkOut.mutateAsync(session.id)
      onStep({ kind: 'sheet', session: row })
    } catch (e) {
      const code = errorCode(e)
      if (code === 'overstay_contact_admin') {
        // The kiosk cannot charge this stay: straight to cash, amount typed.
        addToast({ type: 'warning', message: t('sessions.gate.errors.overstay_contact_admin') })
        onStep({ kind: 'cash', session })
        return
      }
      const known = code && (CHECK_OUT_ERRORS as readonly string[]).includes(code) ? code : 'check_out_failed'
      addToast({ type: 'error', message: t(`sessions.gate.errors.${known}`) })
    }
  }

  return (
    <>
      {canCheckOut && (
        <Button size="sm" onClick={() => void startCheckOut()} loading={checkOut.isPending}>
          {t('sessions.gate.check_out')}
        </Button>
      )}
      {canTakeCash && (
        <Button size="sm" onClick={() => onStep({ kind: 'cash', session })}>
          {t('sessions.gate.take_cash')}
        </Button>
      )}
    </>
  )
}

export interface GateFlowProps {
  step: GateStep | null
  onStepChange: (step: GateStep | null) => void
}

/** The sheet and modals the gate buttons open, rendered once on the page. */
export function GateFlow({ step, onStepChange }: GateFlowProps) {
  const { t } = useTranslation('parking')
  const invalidateSessions = useInvalidateParkingSessions()
  const close = () => onStepChange(null)
  if (!step) return null

  if (step.kind === 'sheet') {
    return (
      <GateCheckOutSheet
        session={step.session}
        onClose={close}
        onTakeCash={() => onStepChange({ kind: 'cash', session: step.session })}
        onVerify={() => onStepChange({ kind: 'verify', session: step.session })}
      />
    )
  }
  if (step.kind === 'cash') return <CashSettleModal session={step.session} onClose={close} />
  if (!step.session.payment) return null
  return (
    <VerifyPaymentModal
      paymentId={step.session.payment.id}
      isOpen
      onClose={close}
      initialReason={t('sessions.gate.verify_at_gate_reason')}
      onSuccess={invalidateSessions}
    />
  )
}
